import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { consultantApi } from '../utils/consultantApi';

// Drives the consultant admin tab. Panels call load* on mount and after writes.
export function useConsultantAgent() {
  const [notes, setNotes] = useState([]);
  const [notesTotal, setNotesTotal] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const loadNotes = useCallback(async (filters = {}) => {
    try {
      setError(null);
      // The route now returns {notes, total}. Total drives the pagination
      // footer in KnowledgePanel; rows is the visible page. Filters that
      // produce zero results still resolve to {notes: [], total: 0} so
      // the UI can show a proper empty state.
      const { notes: rows, total } = await consultantApi.listNotes(filters);
      setNotes(rows || []);
      setNotesTotal(typeof total === 'number' ? total : (rows?.length ?? 0));
    } catch (e) { setError(e.message); }
  }, []);

  const loadDocuments = useCallback(async () => {
    try {
      const { documents: rows } = await consultantApi.listDocuments();
      setDocuments(rows);
    } catch (e) { setError(e.message); }
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const { messages: rows } = await consultantApi.listMessages();
      setMessages(rows);
    } catch (e) { setError(e.message); }
  }, []);

  // Upload a file to Storage, then register it for distillation.
  const uploadDocument = useCallback(async (file) => {
    setBusy(true); setError(null);
    try {
      const { data: s } = await supabase.auth.getSession();
      const userId = s?.session?.user?.id;
      if (!userId) throw new Error('نشست منقضی شده است.');
      const path = `${userId}/${Date.now()}-${crypto.randomUUID()}-${file.name}`;
      const { error: upErr } = await supabase.storage
        .from('consultant-documents')
        .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false });
      if (upErr) throw new Error(`بارگذاری فایل ناموفق بود: ${upErr.message}`);
      await consultantApi.registerDocument({
        file_path: path, file_name: file.name, mime_type: file.type || null,
      });
      await loadDocuments();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }, [loadDocuments]);

  const sendMessage = useCallback(async (content) => {
    setBusy(true); setError(null);
    // Optimistically show the admin's message.
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: 'admin', content }]);
    try {
      const { reply, changes } = await consultantApi.sendMessage(content);
      setMessages((m) => [...m, { id: `tmp-a-${Date.now()}`, role: 'agent', content: reply, changes }]);
      if (changes && changes.length) await loadNotes();
    } catch (e) {
      setError(e.message);
      setMessages((m) => [...m, { id: `tmp-e-${Date.now()}`, role: 'agent', content: `خطا: ${e.message}` }]);
    } finally { setBusy(false); }
  }, [loadNotes]);

  const loadWaitlist = useCallback(async () => {
    try {
      const { waitlist: rows } = await consultantApi.listWaitlist();
      setWaitlist(rows);
    } catch (e) { setError(e.message); }
  }, []);

  // Kicks off a background dreaming job and returns {job_id, status:'pending'}
  // immediately. The KnowledgePanel watches the job row via supabase realtime
  // (admin-read policy on dreaming_jobs) and calls loadNotes() itself when
  // status flips to 'completed' so the new merged notes appear.
  const runDreaming = useCallback(async (category) => {
    setError(null);
    try {
      return await consultantApi.runDreaming(category); // { job_id, status }
    } catch (e) { setError(e.message); return null; }
  }, []);

  return {
    notes, notesTotal, documents, messages, waitlist, busy, error,
    loadNotes, loadDocuments, loadMessages, loadWaitlist,
    uploadDocument, sendMessage, runDreaming,
    api: consultantApi,
    clearError: () => setError(null),
  };
}
